import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HISTORICO, paginaDoHistorico, filtrarHistorico } from "@/lib/historicoVersoes";
import { History, Search, Sparkles, Wrench, CheckCircle2, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

// Histórico de mudanças (padrão do GEDDOC): abre ao clicar na versão do rodapé do menu. O conteúdo
// vem de lib/historicoVersoes.js. Paginado, com pesquisa e filtro por tipo.

const POR_PAGINA = 5;
const TIPOS = {
  novo: { rotulo: "Novo", classe: "bg-emerald-50 text-emerald-700 border-emerald-200", Icone: Sparkles },
  melhoria: { rotulo: "Melhoria", classe: "bg-sky-50 text-sky-700 border-sky-200", Icone: CheckCircle2 },
  correcao: { rotulo: "Correção", classe: "bg-amber-50 text-amber-700 border-amber-200", Icone: Wrench },
};
const FILTROS = [
  { key: "all", rotulo: "Tudo" },
  { key: "novo", rotulo: "Novidades" },
  { key: "melhoria", rotulo: "Melhorias" },
  { key: "correcao", rotulo: "Correções" },
];
const SEMANA = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];

function dataLonga(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return { curta: iso, dia: "" };
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return { curta: `${m[3]}/${m[2]}/${m[1]}`, dia: SEMANA[d.getDay()] };
}

export default function HistoricoVersoesModal({ open, onOpenChange, versaoAtual, historico = HISTORICO }) {
  const [busca, setBusca] = useState("");
  const [tipo, setTipo] = useState("all");
  const [pagina, setPagina] = useState(1);

  const filtrado = useMemo(() => filtrarHistorico(historico, busca, tipo), [historico, busca, tipo]);
  useEffect(() => { setPagina(1); }, [busca, tipo]);
  const pg = paginaDoHistorico(filtrado, pagina, POR_PAGINA);
  const contagem = useMemo(() => {
    const c = { all: 0, novo: 0, melhoria: 0, correcao: 0 };
    for (const e of historico) for (const it of e.itens) { c.all += 1; c[it.tipo] = (c[it.tipo] || 0) + 1; }
    return c;
  }, [historico]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* flex em coluna dentro da altura máxima da janela (janela-base): só a lista rola */}
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 rounded-xl flex flex-col" data-testid="historico-versoes">
        <div className="shrink-0 border-b border-neutral-100 px-5 py-4" style={{ background: "linear-gradient(135deg,#0F172A 0%,#1E3A8A 100%)" }}>
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-white">
              <History className="w-5 h-5" /> Histórico de mudanças
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-white/80">
              {versaoAtual ? `Versão no ar: ${versaoAtual} · ` : ""}o que mudou no sistema em cada atualização
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="shrink-0 flex flex-wrap items-center gap-2 border-b border-neutral-100 bg-white px-5 py-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} data-testid="historico-busca"
                   placeholder="Pesquisar no histórico (ex.: indicação, campanha, SMS)"
                   className={`h-9 w-full rounded-lg border pl-8 pr-3 text-[13px] outline-none focus:ring-2 focus:ring-blue-500/15 ${busca.trim() ? "border-emerald-400 bg-emerald-50/40" : "border-neutral-200"}`} />
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTROS.map((f) => (
              <button key={f.key} type="button" onClick={() => setTipo(f.key)} data-testid={`historico-filtro-${f.key}`}
                      className={`rounded-lg border px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${
                        tipo === f.key ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"}`}>
                {f.rotulo} <span className="opacity-70">({contagem[f.key] || 0})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 md:max-h-[58vh] overflow-y-auto bg-[#f8fafc] px-5 py-4" data-testid="historico-lista">
          {pg.itens.length === 0 ? (
            <p className="py-10 text-center text-sm text-neutral-500" data-testid="historico-vazio">Nada encontrado no histórico com essa pesquisa.</p>
          ) : (
            <ol className="relative space-y-4 border-l-2 border-sky-100 pl-5">
              {pg.itens.map((e) => {
                const d = dataLonga(e.data);
                return (
                  <li key={e.data} className="relative" data-testid={`historico-entrada-${e.data}`}>
                    <span className="absolute -left-[29px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-[#1E3A8A] shadow" />
                    <div className="rounded-xl border border-neutral-100 bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-2 py-0.5 text-xs font-bold text-[#1E3A8A]">
                          <CalendarDays className="w-3.5 h-3.5" /> {d.curta}
                        </span>
                        {d.dia && <span className="text-[11px] text-neutral-400">{d.dia}</span>}
                      </div>
                      <p className="mt-1.5 text-[13.5px] font-semibold text-neutral-900">{e.titulo}</p>
                      <ul className="mt-2 space-y-1.5">
                        {e.itens.map((it, i) => {
                          const t = TIPOS[it.tipo] || TIPOS.melhoria;
                          return (
                            <li key={i} className="flex items-start gap-2 text-[13px] text-neutral-700">
                              <span className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold ${t.classe}`}>
                                <t.Icone className="w-3 h-3" /> {t.rotulo}
                              </span>
                              <span className="min-w-0 leading-relaxed">
                                {it.texto}
                                {it.modulo && <span className="ml-1.5 whitespace-nowrap rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500">{it.modulo}</span>}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {pg.total > POR_PAGINA && (
          <div className="shrink-0 flex items-center justify-between border-t border-neutral-100 bg-white px-4 py-2 text-[12px] text-neutral-600">
            <span>Página {pg.pagina} de {pg.totalPaginas}</span>
            <div className="flex gap-1">
              <button type="button" disabled={pg.pagina <= 1} onClick={() => setPagina(pg.pagina - 1)}
                      className="btn-ghost h-8 px-2 disabled:opacity-40" aria-label="Página anterior"><ChevronLeft size={14} /></button>
              <button type="button" disabled={pg.pagina >= pg.totalPaginas} onClick={() => setPagina(pg.pagina + 1)}
                      className="btn-ghost h-8 px-2 disabled:opacity-40" aria-label="Próxima página"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
